import { z } from "zod";

import {
  AssessmentAuthorSchema,
  AttributedBySchema,
  ConfidenceSchema,
  DatePrecisionSchema,
  DirectionSchema,
  EntityTypeSchema,
  EventTypeSchema,
  LayerSchema,
  PeriodSchema,
  PillarSchema,
  ReliabilitySchema,
  RelTypeSchema,
  SeveritySchema,
  SourceTypeSchema,
  StrengthSchema,
  SubjectTypeSchema,
  TransformSchema,
} from "./enums.js";

/**
 * Domain entities for NEXUS V0.
 *
 * These describe the shape the API speaks, not the shape the database stores.
 * Domain arrays (`layers`, `targetIds`, `sourceIds`, ...) live in join tables
 * on disk and are reconstructed as arrays here — the join tables never appear
 * in this file (MASTER SPEC S10).
 *
 * Three kinds of statement are kept strictly apart (MASTER SPEC S9):
 *   Event        = something that happened at a point in time
 *   Relationship = something that held over an interval
 *   Assessment   = somebody's interpretation of either
 * Nothing in this file lets an interpretation be stored as an observation.
 */

const Id = z.string().min(1);

/** ISO-8601 instant, e.g. "2026-01-15T00:00:00.000Z". */
const Instant = z.iso.datetime();

// ---------------------------------------------------------------------------
// Entity
// ---------------------------------------------------------------------------

export const EntitySchema = z.object({
  id: Id,
  type: EntityTypeSchema,
  nameEn: z.string().min(1),
  nameKo: z.string().min(1),
  countryId: Id.optional(),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  iso2: z.string().length(2).optional(),
  /** Membership of the index normalisation comparison set (MASTER SPEC S24). */
  indexEligible: z.boolean(),
  meta: z.record(z.string(), z.unknown()).default({}),
});
export type Entity = z.infer<typeof EntitySchema>;

// ---------------------------------------------------------------------------
// Technology
// ---------------------------------------------------------------------------

export const TechnologySchema = z.object({
  id: Id,
  layer: LayerSchema,
  nameEn: z.string().min(1),
  nameKo: z.string().min(1),
  parentId: Id.optional(),
});
export type Technology = z.infer<typeof TechnologySchema>;

// ---------------------------------------------------------------------------
// Source
// ---------------------------------------------------------------------------

export const SourceSchema = z
  .object({
    id: Id,
    type: SourceTypeSchema,
    publisher: z.string().min(1),
    title: z.string().min(1),
    url: z.url().optional(),
    publishedAt: Instant.optional(),
    reliability: ReliabilitySchema,
  })
  .refine((s) => !(s.type === "placeholder" && s.url !== undefined), {
    message:
      "a placeholder source must not carry a url: inventing a citation is worse than admitting the gap (RULE 7)",
    path: ["url"],
  });
export type Source = z.infer<typeof SourceSchema>;

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

export const AttributionSchema = z.object({
  attributedActorId: Id,
  attributedBy: AttributedBySchema,
  attributedById: Id.optional(),
  confidence: ConfidenceSchema,
  disputed: z.boolean(),
  sourceIds: z.array(Id).min(1),
});
export type Attribution = z.infer<typeof AttributionSchema>;

// ---------------------------------------------------------------------------
// Event
// ---------------------------------------------------------------------------

export const GeoSchema = z.object({
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
});
export type Geo = z.infer<typeof GeoSchema>;

const EventBase = z.object({
  id: Id,
  occurredAt: Instant,
  precision: DatePrecisionSchema,
  layers: z.array(LayerSchema).min(1),
  titleEn: z.string().min(1),
  titleKo: z.string().min(1),
  summaryKo: z.string().min(1),
  actorId: Id,
  targetIds: z.array(Id),
  eventType: EventTypeSchema,
  geo: GeoSchema.optional(),
  severity: SeveritySchema,
  sourceIds: z.array(Id).min(1),
  attribution: AttributionSchema.optional(),
});

/**
 * Attribution rules (MASTER SPEC S14).
 *
 * A cyber operation always needs an attribution record, because the interesting
 * claim about a cyber operation is *who did it* and how confidently that is
 * known. When nobody knows, that is expressed as `actorId: "unknown"` plus an
 * attribution stating as much — never by silently omitting the question.
 *
 * Military activity is different: an announced exercise has no attribution
 * question at all. It needs a record only when the actor is unknown or the
 * attribution is contested.
 */
export const EventSchema = EventBase.superRefine((event, ctx) => {
  if (event.eventType === "cyber_operation" && event.attribution === undefined) {
    ctx.addIssue({
      code: "custom",
      path: ["attribution"],
      message:
        "cyber_operation requires an attribution record; if the actor is unknown use actorId 'unknown' and say so in the attribution",
    });
  }

  if (
    event.eventType === "military_activity" &&
    event.actorId === "unknown" &&
    event.attribution === undefined
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["attribution"],
      message:
        "military_activity with an unknown actor requires an attribution record",
    });
  }
});
export type Event = z.infer<typeof EventSchema>;

// ---------------------------------------------------------------------------
// Relationship
// ---------------------------------------------------------------------------

/**
 * A relationship is an interval, read half-open: `validFrom <= asOf < validTo`.
 * That choice keeps a superseded relationship and its replacement from both
 * appearing active on the changeover date.
 */
export const RelationshipSchema = z
  .object({
    id: Id,
    fromId: Id,
    toId: Id,
    relType: RelTypeSchema,
    technologyId: Id.optional(),
    strength: StrengthSchema,
    validFrom: Instant,
    validTo: Instant.optional(),
    sourceIds: z.array(Id).min(1),
    supersededByEventId: Id.optional(),
  })
  .refine(
    (r) => r.validTo === undefined || new Date(r.validFrom) < new Date(r.validTo),
    {
      message: "validFrom must be strictly before validTo",
      path: ["validTo"],
    },
  );
export type Relationship = z.infer<typeof RelationshipSchema>;

// ---------------------------------------------------------------------------
// Assessment  (RULE 1)
// ---------------------------------------------------------------------------

/**
 * The only place an interpretation may live.
 *
 * Every assessment must cite at least one source and state its confidence, and
 * a model-authored assessment must name the model. Without those, an inference
 * would be indistinguishable from an observation once it is in the database.
 */
export const AssessmentSchema = z
  .object({
    id: Id,
    subjectType: SubjectTypeSchema,
    subjectId: Id,
    claimKo: z.string().min(1),
    confidence: ConfidenceSchema,
    sourceIds: z.array(Id).min(1),
    author: AssessmentAuthorSchema,
    modelName: z.string().min(1).optional(),
    createdAt: Instant,
  })
  .refine((a) => a.author !== "model" || a.modelName !== undefined, {
    message: "a model-authored assessment must record which model produced it",
    path: ["modelName"],
  });
export type Assessment = z.infer<typeof AssessmentSchema>;

// ---------------------------------------------------------------------------
// Indicator
// ---------------------------------------------------------------------------

export const IndicatorSchema = z.object({
  id: Id,
  pillar: PillarSchema,
  nameKo: z.string().min(1),
  unit: z.string().min(1),
  direction: DirectionSchema,
  transform: TransformSchema,
  sourceIds: z.array(Id).min(1),
  /**
   * Required and non-trivial. Where an indicator depends on a threshold or a
   * boundary that a reader could reasonably draw elsewhere, this note is where
   * that choice is stated, so the number can be argued with.
   */
  methodologyNote: z.string().min(1),
});
export type Indicator = z.infer<typeof IndicatorSchema>;

export const IndicatorValueSchema = z
  .object({
    indicatorId: Id,
    entityId: Id,
    period: PeriodSchema,
    rawValue: z.number().finite(),
    sourceId: Id,
    isEstimate: z.boolean(),
    assessmentId: Id.optional(),
  })
  .refine((v) => !v.isEstimate || v.assessmentId !== undefined, {
    message:
      "an estimated value must point at the Assessment that produced it (RULE 1)",
    path: ["assessmentId"],
  });
export type IndicatorValue = z.infer<typeof IndicatorValueSchema>;

// ---------------------------------------------------------------------------
// WeightSet
// ---------------------------------------------------------------------------

/** Floating-point tolerance for the weights-sum-to-one invariant. */
export const WEIGHT_SUM_TOLERANCE = 1e-9;

export const WeightsSchema = z
  .record(PillarSchema, z.number().min(0).max(1))
  .refine(
    (w) => {
      const sum = Object.values(w).reduce<number>((a, b) => a + (b ?? 0), 0);
      return Math.abs(sum - 1) <= WEIGHT_SUM_TOLERANCE;
    },
    { message: "pillar weights must sum to 1.0" },
  );
export type Weights = z.infer<typeof WeightsSchema>;

export const WeightSetSchema = z.object({
  id: Id,
  label: z.string().min(1),
  weights: WeightsSchema,
  createdAt: Instant,
  isSystem: z.boolean(),
});
export type WeightSet = z.infer<typeof WeightSetSchema>;

// ---------------------------------------------------------------------------
// IndexScore  (RULE 2: derived, never authored)
// ---------------------------------------------------------------------------

export const IndexScoreSchema = z.object({
  entityId: Id,
  weightSetId: Id,
  period: PeriodSchema,
  totalScore: z.number(),
  pillarScores: z.record(PillarSchema, z.number().nullable()),
  coverage: z.number().min(0).max(1),
  lowConfidence: z.boolean(),
  computedAt: Instant,
});
export type IndexScore = z.infer<typeof IndexScoreSchema>;
